import { Collection } from '@msw/data'
import { ProductSchema as ps } from 'app/src/msw/companyagreement/schemas/productSchema'
import { productSchema } from 'app/src/msw/companyagreement/schemas/productsSchema'
import type { Product } from 'app/src/services/welfareportal/companyagreement-api'

/**
 * A record that maps category keys to collections of products, where each collection is structured according to the `productSchema`. This collection is used to store products based on their categories, but it is now deprecated in favor of a single `productCollection` that loads all products from a single JSON file.
 * export const productsCollection = {
 *   'welfareArt51/DIRECT.CARBURANTE': new Collection({ schema: productSchema }),
 *   'welfareArt100/DIRECT.SALUTE': new Collection({ schema: productSchema }),
 *   'welfareArt100/DIRECT.TESTI_SCOLASTICI': new Collection({ schema: productSchema }),
 *   'none/DISCOUNT.MOBILITA': new Collection({ schema: productSchema }),
 *   'welfareArt51/REFUND': new Collection({ schema: productSchema }),
 *   'welfareArt100/REFUND': new Collection({ schema: productSchema }),
 *   'none/TRANSFER': new Collection({ schema: productSchema }),
 * }
 * @deprecated This collection is no longer used to store products. It is recommended to use `productCollection` instead, which loads products from a single JSON file.
 */
export const productsCollection: Record<string, Collection<typeof productSchema>> = {}

/**
 * A collection that stores all products according to the `productSchema` (CompanyAgreementApi Product). This collection is initialized by loading product data from a single JSON file, making it easier to manage and access products without needing to categorize them into separate collections.
 */
export const productCollection = new Collection({ schema: ps })

/**
 * Initializes the `productsCollection` by fetching category data from a JSON file and creating a new collection for each category. Each collection is structured according to the `productSchema`.
 * @deprecated This function is no longer used to initialize the products collection. It is recommended to use `initProductsCollection` instead, which loads products from a single JSON file.
 */
export const initProductsCollections = async () => {
  try {
    const jsonData = await (await fetch(`/data/catalog/categories/index.json`)).json()

    jsonData.items.forEach(async (item: string) => {
      productsCollection[item] = new Collection({ schema: productSchema })
    })

    console.log('Initialized productsCollection with keys:', Object.keys(productsCollection))
  } catch (error) {
    console.error('Error loading categories:', error)
  }
}

/**
 * Creates seeds for the `productsCollection` by fetching product data from JSON files corresponding to each category. If a collection already contains data, it will skip seeding for that collection. Each product is created in the respective collection based on its category.
 * @deprecated This function is no longer used to create product seeds. It is recommended to use `initProductsCollection` instead, which loads products from a single JSON file.
 */
export const createProductsCollectionsSeeds = async () => {
  Object.values(productsCollection).forEach(async (collection, index) => {
    const existingData = await collection.findMany()

    if (existingData.length > 0) {
      return
    }

    try {
      const jsonData = await (
        await fetch(`/data/catalog/categories/${Object.keys(productsCollection)[index]}/products.json`)
      ).json()

      jsonData.items.forEach(async (item: Product) => {
        const createdProduct = await collection.create(item)
        console.log('createdProducts', createdProduct)
      })
    } catch (error) {
      console.error('Error loading products:', error)
    }
  })
}

/**
 * @deprecated This function is now used to fetch all products for a given category key from a JSON file and return them as an array of `Product` objects. It is recommended to use `getAllStaticProducts` instead, which loads all products from a single JSON file for easier management and access.
 * Fetches all products for a given category key from a JSON file and returns them as an array of `Product` objects. If there is an error during the fetch operation, it logs the error and returns an empty array.
 * @param key The category key for which to fetch products.
 * @returns A promise that resolves to an array of `Product` objects.
 */
export const getAllStaticProductsByKey = async (key: string): Promise<Product[]> => {
  try {
    const jsonData = await (await fetch(`/data/catalog/categories/${key}/products.json`)).json()
    return (jsonData?.items || []) as Product[]
  } catch (error) {
    console.error('Error loading products:', error)
    return []
  }
}

/**
 * @deprecated This function is now used to fetch all products for a given category from multiple JSON files corresponding to each category key and return them as an array of `Product` objects. It is recommended to use `getAllStaticProducts` instead, which loads all products from a single JSON file for easier management and access.
 * Fetches all products for a given category from multiple JSON files corresponding to each category key and returns them as an array of `Product` objects. If there is an error during the fetch operation, it logs the error and returns an empty array.
 * @param category The category for which to fetch products.
 * @returns A promise that resolves to an array of `Product` objects.
 */
export const getAllStaticProductsByCategory = async (category: string): Promise<Product[]> => {
  try {
    const jsonIndex = await (await fetch(`/data/catalog/categories/index.json`)).json()

    const items = [] as Product[]

    for (const item of jsonIndex.items) {
      if (item.includes(category)) {
        const jsonData = await (await fetch(`/data/catalog/categories/${item}/products.json`)).json()
        items.push(...(jsonData?.items as Product[]))
      }
    }

    return items
  } catch (error) {
    console.error('Error loading products:', error)
    return []
  }
}

/**
 * @deprecated This function is now used to fetch all products from multiple JSON files corresponding to each category key and return them as an array of `Product` objects. It is recommended to use `getAllStaticProducts` instead, which loads all products from a single JSON file for easier management and access.
 * Fetches all products from multiple JSON files corresponding to each category key and returns them as an array of `Product` objects. If there is an error during the fetch operation, it logs the error and returns an empty array.
 * @returns A promise that resolves to an array of `Product` objects.
 */
export const getAllStaticProducts = async (): Promise<Product[]> => {
  try {
    const jsonIndex = await (await fetch(`/data/catalog/categories/index.json`)).json()

    const items = [] as Product[]

    for (const item of jsonIndex.items) {
      const jsonData = await (await fetch(`/data/catalog/categories/${item}/products.json`)).json()
      items.push(...(jsonData?.items as Product[]))
    }

    return items
  } catch (error) {
    console.error('Error loading products:', error)
    return []
  }
}

/**
 * Initializes the `productCollection` by fetching product data from a single JSON file and creating entries in the collection for each product. This function is designed to load all products at once, simplifying the initialization process and ensuring that all products are available in a single collection.
 * @returns A promise that resolves when the `productCollection` has been initialized with all products.
 */
export const createProductsCollectionSeeds = async () => {
  try {
    const products = await fetch('/data/catalog/products.json').then((r) => r.json())

    for (const product of products?.items || []) {
      productCollection.create(product)
    }
  } catch (error) {
    console.error('Error loading categories:', error)
  }
}

/**
 * This function is designed to fetch all products from a single JSON file and return them as an array of `Product` objects. It is intended to be used for initializing the `productCollection` with all available products, making it easier to manage and access product data without needing to categorize them into separate collections. If there is an error during the fetch operation, it logs the error and returns an empty array.
 * @returns A promise that resolves to an array of `Product` objects.
 */
export const getAllStaticProductsCollection = async () => {
  try {
    const products = await fetch('/data/catalog/products.json').then((r) => r.json())

    return (products?.items as Product[]) || []
  } catch (error) {
    console.error('Error loading categories:', error)
    return []
  }
}
